'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth';
import { UserRequirements, ScoredTechnology, getRecommendations, generateEnhancedRecommendations, EnhancedRecommendationResult } from '@/lib/tech-stack-recommender';
import { TechnologyOption, TECH_CATEGORIES } from '@/lib/tech-stack-types';
import TechStackQuestionnaire from '@/components/TechStackQuestionnaire';
import TechStackComparison from '@/components/TechStackComparison';
import TechStackCharts from '@/components/TechStackCharts';
import TechStackADRGenerator from '@/components/TechStackADRGenerator';
import TechStackHeadToHead from '@/components/TechStackHeadToHead';
import TechStackArchitectureDiagram from '@/components/TechStackArchitectureDiagram';
import FavoriteButton from '@/components/dashboard/FavoriteButton';

type View = 'landing' | 'questionnaire' | 'results' | 'compare' | 'charts' | 'adr' | 'head-to-head';

export default function TechStackDecisionPage() {
  const { isAuthenticated, login } = useAuth();
  const [technologies, setTechnologies] = useState<TechnologyOption[]>([]);
  const [isLoadingTech, setIsLoadingTech] = useState(true);
  const [currentView, setCurrentView] = useState<View>('landing');
  const [requirements, setRequirements] = useState<UserRequirements | null>(null);
  const [recommendations, setRecommendations] = useState<ScoredTechnology[]>([]);
  const [enhancedResults, setEnhancedResults] = useState<EnhancedRecommendationResult | null>(null);
  const [selectedTechnology, setSelectedTechnology] = useState<ScoredTechnology | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Fetch technologies from API on mount
  useEffect(() => {
    const fetchTechnologies = async () => {
      try {
        const response = await fetch('/api/public/technologies?limit=500');
        if (!response.ok) throw new Error('Failed to fetch');
        const data = await response.json();
        setTechnologies(data.technologies);
      } catch (err) {
        console.error('Error fetching technologies:', err);
      } finally {
        setIsLoadingTech(false);
      }
    };
    fetchTechnologies();
  }, []);

  const handleQuestionnaireComplete = (reqs: UserRequirements) => {
    setRequirements(reqs);

    // Get enhanced recommendations with alternative stacks and risks
    const enhanced = generateEnhancedRecommendations(technologies, reqs, 10);
    setEnhancedResults(enhanced);
    setRecommendations(enhanced.primary);
    setSelectedTechnology(enhanced.primary[0]);
    setCurrentView('results');
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    // Could filter by category here
    setCurrentView('questionnaire');
  };

  const handleReset = () => {
    setCurrentView('landing');
    setRequirements(null);
    setRecommendations([]);
    setEnhancedResults(null);
    setSelectedTechnology(null);
    setSelectedCategory(null);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-end mb-4">
            {isAuthenticated ? (
              <Link
                href="/tech-stack-dashboard"
                className="px-4 py-2 bg-light-surface dark:bg-surface border border-light-border dark:border-border text-accent rounded-lg hover:bg-accent-muted transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Dashboard
              </Link>
            ) : (
              <button
                onClick={() => login({ returnTo: '/tech-stack-dashboard' })}
                className="px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent-hover transition-colors flex items-center gap-2 font-semibold"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Login to Save Progress
              </button>
            )}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground mb-4">
            Tech Stack Decision Engine
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary max-w-3xl mx-auto">
            Make informed technology decisions with our comprehensive evaluation framework.
            Get personalized recommendations based on your requirements.
          </p>
        </div>

        {/* Navigation breadcrumbs when not on landing */}
        {currentView !== 'landing' && (
          <div className="mb-8 flex items-center justify-between">
            <nav className="flex items-center space-x-2 text-sm">
              <button
                onClick={handleReset}
                className="text-light-text-secondary dark:text-foreground-secondary hover:text-light-text dark:hover:text-foreground transition-colors"
              >
                Home
              </button>
              {currentView !== 'questionnaire' && (
                <>
                  <span className="text-light-text-tertiary dark:text-foreground-muted">/</span>
                  <button
                    onClick={() => setCurrentView('results')}
                    className="text-light-text-secondary dark:text-foreground-secondary hover:text-light-text dark:hover:text-foreground transition-colors"
                  >
                    Results
                  </button>
                </>
              )}
              <span className="text-light-text-tertiary dark:text-foreground-muted">/</span>
              <span className="text-light-text dark:text-foreground capitalize">{currentView}</span>
            </nav>
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground text-sm hover:bg-light-hover dark:hover:bg-cream/10 transition-colors"
            >
              Start Over
            </button>
          </div>
        )}

        {/* View content */}
        {currentView === 'landing' && (
          <LandingView onStartQuestionnaire={() => setCurrentView('questionnaire')} onBrowseCategory={handleCategorySelect} technologies={technologies} />
        )}

        {currentView === 'questionnaire' && (
          <TechStackQuestionnaire onComplete={handleQuestionnaireComplete} />
        )}

        {currentView === 'results' && recommendations.length > 0 && (
          <ResultsView
            recommendations={recommendations}
            selectedTech={selectedTechnology}
            onSelectTech={setSelectedTechnology}
            onCompare={() => setCurrentView('compare')}
            onViewCharts={() => setCurrentView('charts')}
            onGenerateADR={() => setCurrentView('adr')}
            onHeadToHead={() => setCurrentView('head-to-head')}
          />
        )}

        {currentView === 'compare' && recommendations.length > 0 && (
          <div>
            <button
              onClick={() => setCurrentView('results')}
              className="mb-6 flex items-center text-light-text dark:text-foreground hover:text-accent transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Results
            </button>
            <TechStackComparison technologies={recommendations.slice(0, 5)} />
          </div>
        )}

        {currentView === 'charts' && recommendations.length > 0 && (
          <div>
            <button
              onClick={() => setCurrentView('results')}
              className="mb-6 flex items-center text-light-text dark:text-foreground hover:text-accent transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Results
            </button>
            <TechStackCharts technologies={recommendations} />
          </div>
        )}

        {currentView === 'adr' && selectedTechnology && requirements && (
          <div>
            <button
              onClick={() => setCurrentView('results')}
              className="mb-6 flex items-center text-light-text dark:text-foreground hover:text-accent transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Results
            </button>
            <TechStackADRGenerator
              selectedTechnology={selectedTechnology}
              alternatives={recommendations.slice(1, 4)}
              requirements={requirements}
              allRecommendations={recommendations}
              alternativeStacks={enhancedResults?.alternativeStacks || []}
              risks={enhancedResults?.risks || []}
            />
          </div>
        )}

        {currentView === 'head-to-head' && recommendations.length > 0 && (
          <div>
            <button
              onClick={() => setCurrentView('results')}
              className="mb-6 flex items-center text-light-text dark:text-foreground hover:text-accent transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Results
            </button>
            <TechStackHeadToHead
              availableTechnologies={recommendations}
              initialSelection={recommendations.slice(0, 2).map((t) => t.id)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function LandingView({
  onStartQuestionnaire,
  onBrowseCategory,
  technologies,
}: {
  onStartQuestionnaire: () => void;
  onBrowseCategory: (category: string) => void;
  technologies: TechnologyOption[];
}) {
  return (
    <div className="space-y-12">
      {/* Value proposition */}
      <div className="text-center max-w-4xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
            <div className="text-3xl mb-3">📊</div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Comprehensive Analysis</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
              Evaluate 6 key dimensions: compliance, technical, operational, cost, ecosystem, and strategic fit
            </p>
          </div>
          <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
            <div className="text-3xl mb-3">🎯</div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Personalized Recommendations</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
              Get tailored suggestions based on your team size, budget, expertise, and requirements
            </p>
          </div>
          <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
            <div className="text-3xl mb-3">📝</div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Professional Reports</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
              Export Architecture Decision Records as PDF, Markdown, HTML, or JSON
            </p>
          </div>
        </div>

        <button
          onClick={onStartQuestionnaire}
          className="px-8 py-4 bg-accent text-accent-foreground rounded-lg text-lg font-bold hover:bg-accent-hover transition-colors shadow-lg"
        >
          Start Questionnaire
        </button>
      </div>

      {/* Explore Visualizations */}
      <div className="bg-accent-muted dark:bg-gradient-to-br dark:from-accent-muted dark:to-surface border-2 border-border rounded-lg p-8">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="flex-1">
            <div className="text-4xl mb-3">🎯</div>
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-3">Explore Technology Landscape</h2>
            <p className="text-light-text dark:text-foreground-secondary mb-4">
              Visualize all {technologies.length} technologies with interactive charts including Technology Radar,
              Market Share analysis, Maturity vs Community scatter plots, Popularity Trends, and Comparison Heatmaps.
            </p>
            <Link
              href="/tools/tech-stack-decision/visualizations"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors"
            >
              View Visualizations
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="flex-shrink-0">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-light-surface dark:bg-background rounded-lg p-3 text-center border border-light-border dark:border-navy">
                <div className="text-2xl mb-1">🎯</div>
                <div className="text-xs text-light-text-secondary dark:text-foreground-secondary">Tech Radar</div>
              </div>
              <div className="bg-light-surface dark:bg-background rounded-lg p-3 text-center border border-light-border dark:border-navy">
                <div className="text-2xl mb-1">📊</div>
                <div className="text-xs text-light-text-secondary dark:text-foreground-secondary">Market Share</div>
              </div>
              <div className="bg-light-surface dark:bg-background rounded-lg p-3 text-center border border-light-border dark:border-navy">
                <div className="text-2xl mb-1">📈</div>
                <div className="text-xs text-light-text-secondary dark:text-foreground-secondary">Scatter Plot</div>
              </div>
              <div className="bg-light-surface dark:bg-background rounded-lg p-3 text-center border border-light-border dark:border-navy">
                <div className="text-2xl mb-1">🔥</div>
                <div className="text-xs text-light-text-secondary dark:text-foreground-secondary">Heatmap</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Browse by category */}
      <div>
        <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6 text-center">Or Browse by Category</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.entries(TECH_CATEGORIES).map(([key, value]) => {
            const count = technologies.filter(t => t.category === value).length;
            return (
              <button
                key={value}
                onClick={() => onBrowseCategory(value)}
                className="bg-light-surface dark:bg-surface rounded-lg p-4 text-left hover:bg-light-hover dark:hover:bg-surface/70 hover:border-accent border border-transparent transition-all"
              >
                <div className="font-semibold text-light-text dark:text-foreground mb-1 capitalize">
                  {value.replace(/-/g, ' ')}
                </div>
                <div className="text-sm text-light-text-tertiary dark:text-foreground-tertiary">{count} options</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Features */}
      <div className="bg-light-surface dark:bg-surface rounded-lg p-8">
        <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">What You&apos;ll Get</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-accent mb-2">✓ Detailed Scoring</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
              Each technology is scored across 6 dimensions with transparent methodology
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-accent mb-2">✓ Side-by-Side Comparison</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
              Compare top recommendations across all evaluation criteria
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-accent mb-2">✓ Interactive Charts</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
              Visualize trade-offs with radar charts, bar charts, and scatter plots
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-accent mb-2">✓ Export Options</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
              Download professional PDF reports or ADRs in Markdown, HTML, or JSON format
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-accent mb-2">✓ Match Insights</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
              Understand why each technology fits (or doesn&apos;t fit) your needs
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-accent mb-2">✓ Risk Analysis</h3>
            <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
              Identify potential concerns and mitigation strategies
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultsView({
  recommendations,
  selectedTech,
  onSelectTech,
  onCompare,
  onViewCharts,
  onGenerateADR,
  onHeadToHead,
}: {
  recommendations: ScoredTechnology[];
  selectedTech: ScoredTechnology | null;
  onSelectTech: (tech: ScoredTechnology) => void;
  onCompare: () => void;
  onViewCharts: () => void;
  onGenerateADR: () => void;
  onHeadToHead: () => void;
}) {
  return (
    <div className="space-y-8">
      {/* Architecture Diagram - NEW! */}
      <TechStackArchitectureDiagram technologies={recommendations} maxTechnologies={15} />

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={onHeadToHead}
          className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors"
        >
          Head-to-Head Comparison
        </button>
        <button
          onClick={onCompare}
          className="px-6 py-3 bg-light-surface dark:bg-surface text-light-text dark:text-foreground rounded-lg font-semibold hover:bg-light-hover dark:hover:bg-surface/70 transition-colors"
        >
          Compare Top 5
        </button>
        <button
          onClick={onViewCharts}
          className="px-6 py-3 bg-light-surface dark:bg-surface text-light-text dark:text-foreground rounded-lg font-semibold hover:bg-light-hover dark:hover:bg-surface/70 transition-colors"
        >
          View Charts
        </button>
        <button
          onClick={onGenerateADR}
          className="px-6 py-3 bg-light-surface dark:bg-surface text-light-text dark:text-foreground rounded-lg font-semibold hover:bg-light-hover dark:hover:bg-surface/70 transition-colors"
          disabled={!selectedTech}
        >
          Generate ADR
        </button>
      </div>

      {/* Results list */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-light-text dark:text-foreground">
          Top {recommendations.length} Recommendations
        </h2>

        {recommendations.map((tech) => (
          <div
            key={tech.id}
            className={`bg-light-surface dark:bg-surface rounded-lg p-6 border-2 transition-all cursor-pointer ${
              selectedTech?.id === tech.id
                ? 'border-accent'
                : 'border-transparent hover:border-light-hover dark:hover:border-border'
            }`}
            onClick={() => onSelectTech(tech)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-accent text-accent-foreground rounded-full text-sm font-bold">
                    #{tech.rank}
                  </span>
                  <h3 className="text-xl font-bold text-light-text dark:text-foreground">{tech.name}</h3>
                  <span className="text-sm text-light-text-tertiary dark:text-foreground-tertiary capitalize">{tech.category}</span>
                  <FavoriteButton technologyId={tech.id} technologyName={tech.name} size="sm" />
                </div>
                <p className="text-light-text-secondary dark:text-foreground-secondary">{tech.description}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-accent">{tech.score.toFixed(1)}</div>
                <div className="text-sm text-light-text-tertiary dark:text-foreground-tertiary">out of 100</div>
              </div>
            </div>

            {/* Score breakdown */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
              {Object.entries(tech.scoreBreakdown).map(([key, value]) => (
                <div key={key} className="text-center">
                  <div className="text-sm font-semibold text-light-text dark:text-foreground capitalize">{key}</div>
                  <div className="text-lg font-bold text-accent">{Math.round(value * 100)}</div>
                </div>
              ))}
            </div>

            {/* Match reasons */}
            {tech.matchReasons.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-semibold text-light-text-secondary dark:text-foreground-secondary mb-2">✓ Why this fits:</p>
                <ul className="space-y-1">
                  {tech.matchReasons.slice(0, 3).map((reason, i) => (
                    <li key={i} className="text-sm text-foreground/90 flex items-start">
                      <span className="text-accent mr-2">•</span>
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Concerns */}
            {tech.concerns.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-light-text-secondary dark:text-foreground-secondary mb-2">⚠ Considerations:</p>
                <ul className="space-y-1">
                  {tech.concerns.slice(0, 2).map((concern, i) => (
                    <li key={i} className="text-sm text-light-text-secondary dark:text-foreground-secondary flex items-start">
                      <span className="text-light-text-tertiary dark:text-foreground-muted mr-2">•</span>
                      <span>{concern}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tech details */}
            <div className="mt-4 pt-4 border-t border-light-border dark:border-border-muted flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 bg-background rounded text-light-text-secondary dark:text-foreground-secondary">
                License: {tech.license}
              </span>
              <span className="px-2 py-1 bg-background rounded text-light-text-secondary dark:text-foreground-secondary">
                Pricing: {tech.pricing.model}
              </span>
              <span className="px-2 py-1 bg-background rounded text-light-text-secondary dark:text-foreground-secondary">
                Scalability: {tech.technical.scalability}
              </span>
              <span className="px-2 py-1 bg-background rounded text-light-text-secondary dark:text-foreground-secondary">
                Learning: {tech.technical.learningCurve}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
